/* InkNorth motion layer (C3).
   - Section entrances: [data-in-reveal] containers get .is-in once they enter the viewport;
     their [data-in-part] children are numbered (--in-i) so they arrive in reading order.
     The hidden state only applies after this script adds html.in-motion, and anything already
     on screen at start-up is marked .is-in first, so nothing flickers or stays hidden.
   - Price ticket: picking a sheet size updates the stub (price, size, sheet length, link).
   Re-runs on theme editor section reloads. Respects prefers-reduced-motion via CSS. */
(() => {
  const root = document.documentElement;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  const number = (container) => {
    container.querySelectorAll('[data-in-part]').forEach((part, i) => part.style.setProperty('--in-i', i));
  };

  let observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
  }

  const initReveals = (scope) => {
    scope.querySelectorAll('[data-in-reveal]:not([data-in-reveal-ready])').forEach((el) => {
      el.setAttribute('data-in-reveal-ready', '');
      number(el);
      const box = el.getBoundingClientRect();
      const onScreen = box.top < window.innerHeight * 0.92 && box.bottom > 0;
      if (designMode || !observer || onScreen) {
        el.classList.add('is-in');
      } else {
        observer.observe(el);
      }
    });
  };

  const initTickets = (scope) => {
    scope.querySelectorAll('[data-in-ticket]:not([data-in-ticket-ready])').forEach((ticket) => {
      ticket.setAttribute('data-in-ticket-ready', '');
      const options = [...ticket.querySelectorAll('[data-in-size]')];
      const price = ticket.querySelector('[data-in-stub-price]');
      const size = ticket.querySelector('[data-in-stub-size]');
      const link = ticket.querySelector('[data-in-stub-link]');
      const sheet = ticket.querySelector('[data-in-stub-sheet]');
      if (!options.length || !price) return;

      const select = (option, animate) => {
        options.forEach((o) => o.setAttribute('aria-pressed', String(o === option)));
        const apply = () => {
          price.textContent = option.dataset.price;
          if (size) size.textContent = option.dataset.label;
          if (link && option.dataset.link) link.setAttribute('href', option.dataset.link);
          if (sheet) sheet.style.setProperty('--in-len', option.dataset.len || 1);
        };
        if (!animate) return apply();
        price.classList.add('is-swapping');
        window.setTimeout(() => {
          apply();
          price.classList.remove('is-swapping');
        }, 120);
      };

      options.forEach((option) => option.addEventListener('click', () => select(option, true)));
      select(options.find((o) => o.getAttribute('aria-pressed') === 'true') || options[0], false);
    });
  };

  // Gang sheet length picker: slider + popular lengths → price (straight-line between the
  // price points, flagged as an estimate between them), square inches, designs that fit, film strip.
  const money = (n) => '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/\.00$/, '');
  const initSheetPickers = (scope) => {
    scope.querySelectorAll('[data-in-sheet-picker]:not([data-in-picker-ready])').forEach((picker) => {
      picker.setAttribute('data-in-picker-ready', '');
      let points;
      try { points = JSON.parse(picker.dataset.points || '[]').filter((p) => p[0] > 0).sort((a, b) => a[0] - b[0]); } catch (e) { points = []; }
      const range = picker.querySelector('[data-in-range]');
      if (!range || !points.length) return;
      const width = Number(picker.dataset.width) || 22;
      const designH = Number(picker.dataset.designHeight) || 12;
      const min = Number(range.min), max = Number(range.max);
      const out = (sel) => picker.querySelector(sel);
      const chips = [...picker.querySelectorAll('[data-in-len-chip]')];
      const film = out('[data-in-film]');
      const est = out('[data-in-est]');

      const priceFor = (len) => {
        const exact = points.find((p) => p[0] === len);
        if (exact) return { price: exact[1], exact: true };
        if (len <= points[0][0]) return { price: points[0][1], exact: false };
        const last = points[points.length - 1];
        if (len >= last[0]) return { price: last[1] * (len / last[0]), exact: false };
        for (let i = 1; i < points.length; i += 1) {
          const [l1, p1] = points[i - 1], [l2, p2] = points[i];
          if (len < l2) return { price: p1 + ((p2 - p1) * (len - l1)) / (l2 - l1), exact: false };
        }
        return { price: last[1], exact: false };
      };

      const render = () => {
        const len = Number(range.value);
        const { price, exact } = priceFor(len);
        out('[data-in-len]').textContent = len;
        if (price > 0) out('[data-in-price]').textContent = money(price);
        out('[data-in-sqin]').textContent = (len * width).toLocaleString('en-CA');
        out('[data-in-fits]').textContent = Math.floor(len / designH) * 2;
        if (est) est.hidden = exact;
        range.style.setProperty('--in-fill', ((len - min) / (max - min)) * 100 + '%');
        if (film) film.style.setProperty('--in-ratio', Math.max(0.06, len / max));
        chips.forEach((c) => c.setAttribute('aria-pressed', String(Number(c.dataset.inLenChip) === len)));
      };

      range.addEventListener('input', render);
      chips.forEach((chip) => chip.addEventListener('click', () => { range.value = chip.dataset.inLenChip; render(); }));
      render();
    });
  };

  const init = (scope = document) => {
    initReveals(scope);
    initTickets(scope);
    initSheetPickers(scope);
  };

  // Anything still waiting when the visitor reaches the end of the page (it sits too close
  // to the bottom to cross the trigger line) is shown at once.
  let ticking = false;
  const revealAtEnd = () => {
    ticking = false;
    if (window.innerHeight + window.scrollY < document.documentElement.scrollHeight - 4) return;
    document.querySelectorAll('[data-in-reveal]:not(.is-in)').forEach((el) => {
      el.classList.add('is-in');
      if (observer) observer.unobserve(el);
    });
  };
  window.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; window.requestAnimationFrame(revealAtEnd); }
  }, { passive: true });
  window.addEventListener('resize', revealAtEnd, { passive: true });

  init();
  root.classList.add('in-motion');
  revealAtEnd();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
